import { json } from "stream/consumers";
import Space from "../composition/Space";
import Article from "./Article";
import ArticleCollection from "./ArticleCollection";
import BaseBOM from "./BaseBOM";
import Toggles from "@domain/toggles/Toggles"

const repeat = (arr, n) => Array(n).fill(arr).flat();
export default class RegularBOM extends BaseBOM {

    getOrderReferences(articleCollection?: ArticleCollection) {
        const reference = this.getProps().referenceCode
        const quantity = this.getProps().quantity
        const leadtime = articleCollection ? this.getReferenceLeadtime(articleCollection, reference, quantity) : null
        const moq = articleCollection ? this.getReferenceMoq(articleCollection, reference) : null
        return [{
            quantity,
            reference,
            leadtime,
            moq
        }]
    }

    getModel() {
        return this.getProps().model
    }

    getModels() {
        return [this.getProps().model]
    }

    getAllocatedCodes(): any[] {
        if (this.props.articleCode) {
            return [this.props.articleCode]
        } else {
            return []
        }
    }

    getReferenceLeadtime(articleCollection: ArticleCollection, reference: string, quantity: number) {
        const article = articleCollection.getArticle(reference)
        const leadTimeFunction = article?.getProps()?.leadtime || []

        const isPendant = this.getProps().attributes?.pendantHeight?.value
        const isLW = Boolean(this.getProps().attributes?.pendantHeight?.bov !== 'B')

        if (isPendant && isLW) {
            const pendantVariations = articleCollection.getVariationsByType(null, 'pendantHeight')
            const pendantVariation = pendantVariations.find(variation => variation.getModel() === this.getModel())
            const pendantLeadtime = pendantVariation?.getLeadTime()

            if (pendantLeadtime) {
                leadTimeFunction.forEach((range, indexLeadTimeFunction) => {
                    const pendantLeadTimeRange = pendantLeadtime.find(pendantRange => pendantRange.from == range.from)
                    if (!pendantLeadTimeRange || !pendantLeadTimeRange.weeks || !range?.weeks) return
                    if (range.weeks < pendantLeadTimeRange.weeks) leadTimeFunction[indexLeadTimeFunction].weeks = pendantLeadTimeRange.weeks
                })
            }
        }

        let leadtime = null
        for (const leadTimeRange of leadTimeFunction) {
            if (leadTimeRange.from <= quantity) {
                leadtime = leadTimeRange.weeks
            }
        }
        return leadtime
    }

    getAvailabilityDate(articleCollection: ArticleCollection) {
        const reference = this.getProps().referenceCode
        const article = articleCollection.getArticle(reference)
        if (!article) return 0
        const articleVariations = articleCollection.getArticleVariations(article)
        const availabilities = articleVariations.map(articleVariation => articleVariation.timeUntilAvailable('date'))
        return Math.max(...availabilities)
    }

    // TODO: To increase efficiency, we should group exact same articles and change the contract from:
    // Article[] to: {article: Article, quantity: number} []
    getArticles(articleCollection?: ArticleCollection): Article[] {
        let article
        if (articleCollection) {
            // Gets the full article which was used to create the given BOM
            article = articleCollection.getArticle(this.props.referenceCode)

            if (this.props.priceReference) {
                article.props.priceReference = this.props.priceReference
            }
            if (!article) return []
            if (this.props.attributes?.pendantHeight?.value) {
                article.setPendantHeight(this.props.attributes.pendantHeight.value / 100)
            }
        } else {
            // Does a PARTIAL reconstruction of the article which was used to create the given BOM.
            const props = this.getProps()
            article = new Article({
                model: props.model,
                reference: props.referenceCode,
                attributes: props.attributes,
                family: {
                    id: props.familyId
                },
                subFamily: props.subFamily,
                sublevel: props.sublevel,
                hasAttributes: props.hasAttributes,
                applicationsLocations: [props.applicationLocation],
                lumensLed: props.lumensLed,
                watts: props.watts,
                articleCode: props.articleCode,
                priceReference: props.priceReference
            })
        }
        return repeat([article], this.props.quantity)
    }

    refreshCodes(articleCollection, allocatedCodes, counters) {
        const article = new Article({
            model: this.props.model,
            attributes: this.props.attributes,
            reference: this.props.referenceCode
        }) // TODO: Falta un conversor BOM -> [Articles]

        if (articleCollection.articleNeedsLWCode(article)) {
            const allocated = allocatedCodes.filter(code => code.includes('LW')) // Filter those that contain LW
            const { code, counter } = BaseBOM.calculateCode(`${article.getModel()}LW`, allocated, counters.LW)
            this.props.articleCode = code
            return { BOM: this, counters: { ...counters, LW: counter } }
        }
        return { BOM: this, counters }
    }

    static fromArticle(article: Article, space?: Space, quantity?: number) {
        const BOM: any = {
            ...this.parseArticleProperties(article),
            configuratorType: BaseBOM.getConfiguratorType(space),
            type: "REGULAR",
            quantity: 1,
            isPersonalized: article.isPersonalized() ? 1 : 0,
            validation: {
                status: 0
            },
        }
        const regularBOM = new RegularBOM(BOM)

        // Preserve the saved quantity when editing an existing product.
        if (quantity === undefined) return regularBOM

        regularBOM.setQuantity(quantity)
        return regularBOM
    }
}
